import { Accordion, Button, MenuTrigger, Stack, Text } from '@eleveo/components-react';
import { FilterClearIcon, ThreeDotsIcon } from '@eleveo/icons-react';
import { equals } from 'ramda';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { Trans, useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import {
  CheckboxFilterTile,
  getCheckboxFilterTileSubtitle,
} from '../../components/LayoutNext/components/CheckboxFilterTile';
import { DateRangeFilterTile } from '../../components/LayoutNext/components/DateRangeFilterTile';
import { isDateRangeFilterAccordionType } from '../../components/LayoutNext/components/filterTileUtils';
import { Queue } from '../../graphql/components';
import { DateRangeFilter } from '../../utils/filter';
import {
  ACTIVE_SIDEBAR_FILTERS_COUNTER,
  APPLY_FILTER_BUTTON_TAG,
  FILTER_SIDEBAR_DROPDOWN_TOGGLE,
  RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
} from './constants';

import type { CheckboxFilterTileProps } from '../../components/LayoutNext/components/CheckboxFilterTile';
import type { DateRangeFilterTileProps } from '../../components/LayoutNext/components/DateRangeFilterTile';

export type ForecastingStudioFilterSidebarState = {
  range: DateRangeFilter;
  queues: string[];
};

export type ForecastingStudioFilterSidebarProps = {
  handleCancel: () => void;
  preOpenedItemIds: string[];
  queues: Queue[];
  setPreOpenedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSidebarFilter: (filter: ForecastingStudioFilterSidebarState) => void;
  sidebarFilter: ForecastingStudioFilterSidebarState;
};

export const ForecastingStudioFilterSidebar: React.FC<ForecastingStudioFilterSidebarProps> = ({
  handleCancel,
  preOpenedItemIds,
  queues,
  setPreOpenedItemIds,
  setSidebarFilter,
  sidebarFilter,
}) => {
  const { timezone } = useAuth();
  const { t } = useTranslation();
  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });

  const defaultSSelectedQueueIds = sidebarFilter.queues;

  // Filters states
  const [selectedQueueIds, setSelectedQueueIds] = useState(defaultSSelectedQueueIds);
  const [selectedRange, setSelectedRange] = useState(sidebarFilter.range);

  const handleApply = () => {
    setSidebarFilter({
      queues: selectedQueueIds,
      range: selectedRange,
    });

    handleCancel();
  };

  const handleResetAllFilters = () => {
    setSelectedQueueIds([]);
    setSelectedRange(null as unknown as DateRangeFilter);

    setPreOpenedItemIds([]);
  };

  const isRangeFilterActive = sidebarFilter.range?.start && sidebarFilter.range?.end;

  const countOfActiveFilters = [!!defaultSSelectedQueueIds.length, !!isRangeFilterActive].filter(
    (active) => active
  ).length;

  const isFilterActive = selectedQueueIds.length > 0 || isRangeFilterActive;

  const isApplyActive =
    equals(selectedQueueIds, defaultSSelectedQueueIds) &&
    equals(sidebarFilter.range?.start, selectedRange?.start) &&
    equals(sidebarFilter.range?.end, selectedRange?.end);

  // Accordion filter options
  const filters: [DateRangeFilterTileProps, CheckboxFilterTileProps] = [
    {
      testId: 'forecasting-sidebar-filter-range',
      id: '1',
      subtitle:
        selectedRange?.start && selectedRange?.end
          ? `${formatter.format(selectedRange?.start.toDate(timezone))} - ${formatter.format(
              selectedRange?.end.toDate(timezone)
            )}`
          : '',
      subtitleColor: 'info',
      title: t('forecast.forecastRange'),
      values: selectedRange,
      setValues: setSelectedRange,
    },
    {
      testId: 'forecasting-sidebar-filter-queues',
      id: '2',
      items: queues,
      onSelectionChange: setSelectedQueueIds,
      selectedKeys: selectedQueueIds,
      selectionMode: 'single',
      subtitle: getCheckboxFilterTileSubtitle(t, queues, selectedQueueIds),
      subtitleColor: 'info',
      title: t('queue.queue'),
    },
  ];

  return (
    <Sidebar onClose={handleCancel} title={t('filter.filters')}>
      <SidebarPanel>
        <Stack direction="horizontal" className="w-full items-center justify-between">
          <Button testId={APPLY_FILTER_BUTTON_TAG} isDisabled={isApplyActive} color="success" onClick={handleApply}>
            {t('form.apply')}
          </Button>
          <Text
            color={!countOfActiveFilters ? { light: 'grey-500', dark: 'grey-300' } : {}}
            testId={ACTIVE_SIDEBAR_FILTERS_COUNTER}
          >
            <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
              {/* space needs to be added within strong tag so the tests can run correctly */}
              {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
              <strong>{{ countOfActiveFilters }} </strong>Active filters
            </Trans>
          </Text>

          <MenuTrigger
            items={[
              {
                id: '1',
                text: t('filter.resetAllFilters'),
                onClick: handleResetAllFilters,
                icon: <FilterClearIcon />,
                isDisabled: !isFilterActive,
                testId: RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
              },
            ]}
          >
            <Button aria-label={t('filter.options')} variant="outline" testId={FILTER_SIDEBAR_DROPDOWN_TOGGLE}>
              <ThreeDotsIcon />
            </Button>
          </MenuTrigger>
        </Stack>
      </SidebarPanel>

      <SidebarContent>
        <SidebarPart>
          <Accordion
            expandedKeys={preOpenedItemIds}
            items={filters}
            expansionMode="single"
            onExpandedChange={(keys) => setPreOpenedItemIds([...keys] as string[])}
          >
            {(item) => {
              // Calendar Range filter type/content
              if (isDateRangeFilterAccordionType(item)) {
                return <DateRangeFilterTile {...item} />;
              }
              return <CheckboxFilterTile {...item} />;
            }}
          </Accordion>
        </SidebarPart>
      </SidebarContent>
    </Sidebar>
  );
};
